import axios from "axios";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useAxiosHook } from "./useAxiosHook";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { removeEmptyArrays } from "../_src_shared/utils/objects/removeEmptyArrays";

const ROUTE_PREFIX = "underwriting";

export const useUnderwritingHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const error = axiosError;

  // -------------------------------------
  //  WORKING
  //  GET /underwriting/underwriting_pipeline
  // -------------------------------------
  const getUnderwritingPipeline = async ({
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/underwriting_pipeline`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Underwriting Pipeline",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  const populateLoanFormFromExcel = async ({
    excel,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(excel) || isEmpty(excel)) {
      console.error("Excel file is missing", { excel });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/new_loan/populate_loan_form`;
    const source = axios.CancelToken.source();

    // console.log("excel", excel);
    // return;
    await axiosPostRequest({
      name: "Populate Loan Form From Excel",
      path: endpoint,
      // files: { uploadMetadata: [excel] }, // FormData with submission + files
      files: [excel], // FormData with submission + files
      formData: true,
      // errorTesting: true,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoan = async ({ data, onSuccessFn, onCompleteFn, onFailFn }) => {
    if (isNil(data) || isEmpty(data)) {
      console.error("Loan data is missing", { data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_loan`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onSuccessMsg: "Loan created successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanWithFileUploads = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(data) || isEmpty(data)) {
      console.error("Form data is missing", { data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_loan_file_uploads`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan With File Uploads",
      path: endpoint,
      formData: true,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanFromApplication = async ({
    applicationId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(applicationId)) {
      console.error("applicationId is missing", { applicationId });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_deal_from_submission/loanApplication/${applicationId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Loan From Application",
      path: endpoint,
      // formData: true,

      // errorTesting: true,
      cancelSource: source,
      onSuccessFn,

      onCompleteFn,
      onFailFn,
    });
  };

  // Multi use
  const createLoanFromSubmission = async ({
    applicationId,
    submissionType,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      applicationId,
      submissionType, // 'loanApplication', 'quickApp', 'creditAuthorization'
    });
    const endpoint = `${ROUTE_PREFIX}/create_deal_from_submission/${submissionType}/${applicationId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Create Loan From Submission: ${submissionType}`,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,

      onCompleteFn,
      onFailFn,
    });
  };

  const getLoanById = async ({
    loanId,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/loan/${loanId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Get Loan By ID",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosGetRequest(params);
  };

  const updateLoanData = async ({
    loanId,
    data,
    onSuccessFn,
    onSuccessMsg = "Loan data updated successfully",
    onCompleteFn,
    hideFailMsg = false,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_loan_data/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Loan Data",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: onSuccessMsg,
      // errorTesting: true,
      onCompleteFn,
      hideFailMsg,
      onFailFn,
    });
  };

  const updateUnderwritingNotes = async ({
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/underwritingNotes/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Underwriting Notes",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onSuccessMsg: "Underwriting notes updated successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const updateMilestoneStatus = async ({
    loanId,
    loanStatus,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(loanStatus)) {
      console.error("Loan ID or Loan Status is missing", {
        loanId,
        loanStatus,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_milestone/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Milestone Status",
      path: endpoint,
      payload: { loanStatus },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: `Milestone status updated successfully to ${loanStatus}`,
      // errorTesting: true,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateLoanStatusOrSubStatus = async ({
    loanId,
    loanStatus,
    subStatus,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      loanId,
      loanStatus,
    });
    const endpoint = `${ROUTE_PREFIX}/update_milestone/${loanId}`;
    const source = axios.CancelToken.source();
    const payload = { loanStatus, subStatus };
    // console.log("Payload for updating loan status or sub status", payload);
    // return;
    await axiosPostRequest({
      name: "Update Loan Status",
      path: endpoint,
      payload: payload,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onSuccessMsg: `Loan status updated successfully to ${loanStatus}`,
      onCompleteFn,
      onFailFn,
    });
  };

  const createDealComment = async ({
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    testing = false,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_comment/${loanId}`;
    const source = axios.CancelToken.source();

    // console.log("data", data);
    // return;
    await axiosPostRequest({
      name: "Create Deal Comment",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: testing,
      onCompleteFn,
      onFailFn,
    });
  };

  const createEnhancedDealComment = async ({
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    testing = false,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    // const endpoint = `${ROUTE_PREFIX}/create_enhanced_deal_comment/${loanId}`;
    const endpoint = `${ROUTE_PREFIX}/create_comment_with_uploads/${loanId}`;
    const source = axios.CancelToken.source();
    // console.log("data", data);
    // return;
    await axiosPostRequest({
      name: "Create Deal Comment",
      path: endpoint,
      payload: data,
      formData:
        isArray(data?.uploadMetadata) && !isEmpty(data?.uploadMetadata)
          ? true
          : false,
      cancelSource: source,
      onSuccessFn,
      // errorTesting: true,
      onCompleteFn,
      onFailFn,
    });
  };

  const addPermittedUserToLoan = async ({
    loanId,
    emailAddress,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(emailAddress)) {
      console.error("Loan ID or Data is missing", { loanId, emailAddress });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/add_permitted_users/${loanId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Add Permitted User To Loan",
      path: endpoint,
      payload: {
        emailAddress: emailAddress,
      },
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const updateLoanPermittedUsers = async ({
    loanId,
    permittedUsers,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(permittedUsers) || !isArray(permittedUsers)) {
      console.error("Loan ID or Data is missing", { loanId, permittedUsers });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/add_permitted_users/${loanId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Add Permitted User To Loan",
      path: endpoint,
      payload: {
        permittedUsers,
      },
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const createUnnestedObject = async ({
    objectName, //  titleReport', 'underwritingCheckList', 'loanLock', 'borrowerEntity'
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(objectName) || isNil(data)) {
      console.error("Loan ID, Object Name, or Data is missing", {
        loanId,
        objectName,
        data,
      });
      return;
    }

    const objectNameOptions = [
      "titleReport",
      "underwritingCheckList",
      "loanLock",
      "borrowerEntity",
    ];
    if (!objectNameOptions.includes(objectName)) {
      console.error("Invalid object name", { objectName });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_object/${objectName}/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Unnested Object",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateUnnestedObject = async ({
    objectName, //  titleReport', 'underwritingCheckList', 'loanLock', 'borrowerEntity'
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onSuccessMsg = "Updated successfully",
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(objectName) || isNil(data)) {
      console.error("Loan ID, Object Name, or Data is missing", {
        loanId,
        objectName,
        data,
      });
      return;
    }

    const objectNameOptions = [
      "titleReport",
      "underwritingCheckList",
      "loanLock",
      "borrowerEntity",
    ];
    if (!objectNameOptions.includes(objectName)) {
      console.error("Invalid object name", { objectName });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_object/${objectName}/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Unnested Object",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onCompleteFn,
      onFailFn,
    });
  };

  const createLoanException = async ({
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(data) || isEmpty(data)) {
      console.error("Loan ID or Data is missing", { loanId, data });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_loan_exception/${loanId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Create Loan Exception",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const createObjectInList = async ({
    objectName,
    loanId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/create_list_object/${objectName}/${loanId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Create Object In List",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const updateObjectInList = async ({
    objectName,
    loanId,
    _id,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/update_list_object/${objectName}/${loanId}/${_id}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Update Object In List",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const createSubjectProperty = async ({
    loanId,
    propertyData,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(propertyData)) {
      console.error("Loan ID or propertyData is missing", {
        loanId,
        propertyData,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_subject_property/${loanId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Subject Property",
      path: endpoint,
      payload: propertyData,
      cancelSource: source,
      onSuccessMsg: "Property created successfully",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateSubjectProperty = async ({
    loanId,
    propertyId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/update_subject_property/${loanId}/${propertyId}`;
    const source = axios.CancelToken.source();
    if (isNil(loanId) || isNil(propertyId) || isNil(data)) {
      console.error("Loan ID or Property ID is missing", {
        loanId,
        propertyId,
        data,
      });
      return;
    }

    await axiosPostRequest({
      name: "Update Subject Property",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg: "Property updated successfully",
      // errorTesting: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createSubjectPropertyUnit = async ({
    loanId,
    propertyId,
    propertyData,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(propertyData) || isNil(propertyId)) {
      console.error("Loan ID or propertyData is missing", {
        loanId,
        propertyData,
        propertyId,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/create_unit/${loanId}/${propertyId}`;
    const source = axios.CancelToken.source();
    // console.log("Submitting", { loanId, propertyId, propertyData });
    // return;
    await axiosPostRequest({
      name: "Create Subject Property Unit",
      path: endpoint,
      payload: propertyData,
      cancelSource: source,
      onSuccessMsg: "Property created successfully",
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateSubjectPropertyUnit = async ({
    loanId,
    propertyId,
    unitId,
    unitData,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/update_unit/${loanId}/${propertyId}/${unitId}`;
    const source = axios.CancelToken.source();
    if (
      isNil(loanId) ||
      isNil(propertyId) ||
      isNil(unitData) ||
      isNil(unitId)
    ) {
      console.error("Loan ID or Property ID is missing", {
        loanId,
        propertyId,
        unitData,
      });
      return;
    }

    // console.log("--------", { loanId, propertyId, unitId, unitData });
    // return;
    await axiosPostRequest({
      name: "Update Subject Property Unit",
      path: endpoint,
      payload: unitData,
      cancelSource: source,
      onSuccessMsg: "Property updated successfully",
      // errorTesting: true,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createBorrower = async ({
    loanId,
    borrower,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/create_borrower/${loanId}`;
    const source = axios.CancelToken.source();

    if (isNil(loanId) || isNil(borrower)) {
      console.error("Loan ID or Borrower data is missing", {
        loanId,
        borrower,
      });
      return;
    }
    await axiosPostRequest({
      name: "Create Borrower",
      path: endpoint,
      payload: borrower,
      cancelSource: source,
      onSuccessMsg: "Borrower created successfully",

      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateBorrower = async ({
    loanId,
    borrowerId,
    data,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    if (isNil(loanId) || isNil(borrowerId) || isNil(data)) {
      console.error("Loan ID or Borrower ID is missing", {
        loanId,
        borrowerId,
        data,
      });
      return;
    }
    const endpoint = `${ROUTE_PREFIX}/update_borrower/${loanId}/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Borrower",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessMsg: "Borrower updated successfully",

      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const assignSubmissionToDeal = async ({
    borrower_submission,
    loanNumber,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/assign_to_deal/${borrower_submission}/${loanNumber}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Assign Submission To Deal",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const addLoanTag = async ({ dealId, tags, onSuccessFn, onFailFn }) => {
    if (isNil(dealId) || isNil(tags)) {
      console.error("Deal ID or Tag is missing", { dealId, tags });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/add_loan_tag/${dealId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Add Loan Tag",
      path: endpoint,
      payload: { tags: tags },
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const removeLoanTag = async ({ dealId, tag, onSuccessFn, onFailFn }) => {
    if (isNil(dealId) || isNil(tag)) {
      console.error("Deal ID or Tag is missing", { dealId, tag });
      return;
    }

    // console.log("Tag", tag);
    // return;
    const endpoint = `${ROUTE_PREFIX}/remove_loan_tag/${dealId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Remove Loan Tag",
      path: endpoint,
      payload: { tags: [tag] },
      // payload: data,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const addLoanRelatedContact = async ({
    dealId,
    contactData,
    onSuccessFn,
    onFailFn,
  }) => {
    if (isNil(dealId) || isNil(contactData)) {
      console.error("Deal ID or Contact Data is missing", {
        dealId,
        contactData,
      });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/add_loan_related_contact/${dealId}`;
    const source = axios.CancelToken.source();
    const params = {
      name: "Add Loan Related Contact",
      path: endpoint,
      payload: contactData,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    };
    await axiosPostRequest(params);
  };

  const updateLoanRelatedContact = async ({
    dealId,
    contactId,
    contactData,
    onSuccessFn,
    onFailFn,
  }) => {
    if (isNil(dealId) || isNil(contactId) || isNil(contactData)) {
      console.error("Deal ID or Contact Data is missing", {
        dealId,
        contactId,
        contactData,
      });
      return;
    }

    const endpoint = `${ROUTE_PREFIX}/update_loan_related_contact/${dealId}/${contactId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Loan Related Contact",
      path: endpoint,
      payload: contactData,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const addAssociation = async ({
    dealId,
    associationData,
    associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
    associationTypeId,
    onSuccessFn,
    onFailFn,
  }) => {
    /**
      loanDocuments : List[PyObjectId] | None = Field(default = [], description = 'list of documents associated with the comment')
      borrowers : List[PyObjectId] | None = Field(default = None, description = 'list of borrower ids associated with the comment')
      subjectProperties : List[PyObjectId] | None = Field(default = None, description = 'list of subject property ids associated with the comment')
      applications : List[PyObjectId] | None = Field(default = None, description = 'list of application ids associated with the comment')
      creditAuthorizations : List[PyObjectId] | None = Field(default = None, description = 'list of credit authorization ids associated with the comment')
      conditions : List[PyObjectId] | None = Field(default = None, description = 'list of underwriting condition ids associated with the comment')
      taskListItems : List[PyObjectId] | None = Field(default = None, description = 'list of task list item ids associated with the comment')
      managementExceptions : List[PyObjectId] | None = Field(default = None, description = 'list of management exception ids associated with the comment')
      comments : List[PyObjectId] | None = Field(default = None, description = 'list of comment ids associated with the comment')
      tasks : List[PyObjectId] | None = Field(default = None, description = 'list of task ids associated with the comment')
      */
    // console.log("Creating association", {
    //   dealId,
    //   associationData,
    //   associationTypeName,
    //   associationTypeId,
    // });
    validateRequiredFields({
      dealId,
      associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
      associationTypeId,
      associationData,
    });
    const endpoint = `${ROUTE_PREFIX}/create_association/${dealId}/${associationTypeName}/${associationTypeId}`;
    const source = axios.CancelToken.source();

    const cleanedPayload = removeEmptyArrays(associationData);
    // console.log("--- ADD REQUEST --- ", {
    //   name: `Add Association to ${associationTypeName}`,
    //   path: endpoint,
    //   payload: cleanedPayload,
    //   cancelSource: source,
    //   onSuccessFn,
    //   onFailFn,
    // });
    // if (onSuccessFn) {
    //   onSuccessFn();
    // }
    // return;
    await axiosPostRequest({
      name: `Add Association to ${associationTypeName}`,
      path: endpoint,
      payload: cleanedPayload,

      // payload: {
      //   borrowers: [],
      //   subjectProperties: [],
      //   loanDocuments: ["68da9bef9c0656e40667b68c"],
      // },
      // payload: {
      //   association: {
      //     borrowers: [],
      //     subjectProperties: [],
      //     loanDocuments: ["68da9bef9c0656e40667b68c"],
      //   },
      // },
      // payload: associationData,
      // payload: {
      //   associations: associationData,
      // },
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const removeAssociation = async ({
    dealId,
    associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
    associationTypeId, // borrwer?.id
    associationIdToDelete,
    associationToDeleteName = "loanDocuments", // 'borrowers', 'subjectProperties', 'loanDocuments'
    onSuccessFn,
    onFailFn,
  }) => {
    validateRequiredFields({
      dealId,
      associationIdToDelete,
      associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
      associationTypeId,
      associationToDeleteName,
    });

    /**
     * @router.post("/delete_association/{dealId}/{referenceObjectName}/{referenceObjectId}/{associatedObjectName}/{remove_association_id}")
     * @router.post("/delete_association/{dealId}/{'borrowers'}/{borrowers[0]?._id}/{'loanDocuments'}/{remove_association_id}")
     */
    const endpoint = `${ROUTE_PREFIX}/delete_association/${dealId}/${associationTypeName}/${associationTypeId}/${associationToDeleteName}/${associationIdToDelete}`;
    const source = axios.CancelToken.source();
    // console.log("Removing association", {
    //   dealId,
    //   associationTypeName, // 'borrowers', 'subjectProperties', 'documents'
    //   associationTypeId, // borrwer?.id
    //   associationIdToDelete,
    // });

    // console.log("--- REMOVE REQUEST --- ", {
    //   name: `Remove Associations to ${associationTypeName}`,
    //   path: endpoint,
    //   payload: {},
    //   cancelSource: source,
    //   onSuccessFn,
    //   onFailFn,
    // });
    // if (onSuccessFn) {
    //   onSuccessFn();
    // }
    // return;
    await axiosPostRequest({
      name: `Remove Associations to ${associationTypeName}`,
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const markDealClosed = async ({ dealId, onSuccessFn, onFailFn }) => {
    validateRequiredFields({
      dealId,
    });

    const endpoint = `${ROUTE_PREFIX}/mark_deal_closed/${dealId} `;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Deal Marked Closed Successfully`,
      path: endpoint,
      payload: {},
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const markDealDead = async ({ dealId, payload, onSuccessFn, onFailFn }) => {
    validateRequiredFields({
      dealId,
      payload,
    });

    const endpoint = `${ROUTE_PREFIX}/mark_deal_dead/${dealId} `;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: `Deal Marked Dead Successfully`,
      path: endpoint,
      payload: payload,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  const getZillowZipcodeData = async ({ dealId, onSuccessFn, onFailFn }) => {
    validateRequiredFields({
      dealId,
    });

    const endpoint = `${ROUTE_PREFIX}/get_zillow_zipcode_data/${dealId} `;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      // name: `Zillow Zipcode Data Retrieved Successfully`,
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onFailFn,
    });
  };

  return {
    error,
    loading,
    getUnderwritingPipeline,
    getLoanById,
    updateLoanData,
    updateMilestoneStatus,
    createDealComment,
    createEnhancedDealComment,
    createUnnestedObject,
    createSubjectProperty,
    updateSubjectProperty,
    createBorrower,
    updateBorrower,
    assignSubmissionToDeal,
    updateLoanPermittedUsers,
    populateLoanFormFromExcel,
    createLoanWithFileUploads,
    createLoan,
    updateUnderwritingNotes,
    addPermittedUserToLoan,
    removeAssociation,
    updateUnnestedObject,
    createLoanException,
    createObjectInList,
    updateObjectInList,
    createLoanFromApplication,
    removeLoanTag,
    updateLoanRelatedContact,
    addLoanTag,
    addLoanRelatedContact,
    createSubjectPropertyUnit,
    updateSubjectPropertyUnit,
    addAssociation,
    markDealClosed,
    markDealDead,
    getZillowZipcodeData,
    createLoanFromSubmission,
    updateLoanStatusOrSubStatus,
  };
};
